/**
 * trialFacts v0.1 (2026-10-08, cc_tasks row 520, T560)
 *
 * What a new account's trial includes, read from the account and the plan settings, never typed.
 * Shared by the quiet first-screen line (OnboardingResumeBanner), the sidebar (which hides its
 * START HERE card while that line shows) and the header pill (CreditGauge). The approved spec is
 * https://mentionfox.com/preview/first-screen-31l8p9m0abcv41vg ("Proposed"), traced in T541:
 *   trial length   = user_credits_v2.trial_ends_at minus profiles.created_at, in whole days
 *   Pro features   = TIER_LIMITS.trial equals TIER_LIMITS.pro (src/lib/inngest/tier-limits.ts)
 *   companies      = watch_plan_limits for the account's tier (max_companies, alert_mode)
 *   credits        = user_credits_v2.monthly_credit_grant (the trial's grant on this account)
 * A part that cannot be read is left out of the sentence; nothing is guessed.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { TIER_LIMITS } from "@/lib/inngest/tier-limits";

const DAY_MS = 86_400_000;

export interface TrialFacts {
  tier: string | null;
  trialEndsAt: string | null;
  /** Whole days from sign-up to the trial's end (null when unknown). */
  trialLengthDays: number | null;
  proFeatures: boolean;
  watchMax: number | null;
  watchMode: string | null;
  credits: number | null;
}

/** Tiers that are not paid. Anything else with a value is a paying (or staff) account. */
const UNPAID = new Set(["trial", "free"]);

export function isPaidTier(tier: string | null | undefined): boolean {
  const t = String(tier ?? "").toLowerCase();
  return t !== "" && !UNPAID.has(t);
}

/** Days left for the header pill: 1 on the last day, never 0 or negative; null once the trial has ended. */
export function trialDaysLeftCeil(trialEndsAt: string | null | undefined, now: number = Date.now()): number | null {
  if (!trialEndsAt) return null;
  const ms = new Date(trialEndsAt).getTime() - now;
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return Math.max(1, Math.ceil(ms / DAY_MS));
}

export function trialRunning(f: TrialFacts | null, now: number = Date.now()): boolean {
  return !!f && String(f.tier ?? "").toLowerCase() === "trial" && trialDaysLeftCeil(f.trialEndsAt, now) !== null;
}

function joinParts(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

/** What the trial includes, as the phrase after "includes" (empty when nothing could be read). */
export function trialIncludes(f: TrialFacts): string {
  const parts: string[] = [];
  if (f.proFeatures) parts.push("the Pro plan's features");
  if (f.watchMax && f.watchMax > 0) {
    const mode = f.watchMode === "instant" ? " with instant alerts" : f.watchMode === "daily_digest" ? " with a daily digest" : "";
    parts.push(`watching up to ${f.watchMax} ${f.watchMax === 1 ? "company" : "companies"}${mode}`);
  }
  if (f.credits !== null && f.credits > 0) parts.push(`${f.credits} ${f.credits === 1 ? "credit" : "credits"}`);
  return joinParts(parts);
}

/** "Your free trial runs 5 days and includes ..." or null when no trial is running. */
export function trialSentence(f: TrialFacts | null, now: number = Date.now()): string | null {
  if (!f || !trialRunning(f, now)) return null;
  const inc = trialIncludes(f);
  const len = f.trialLengthDays && f.trialLengthDays > 0
    ? `runs ${f.trialLengthDays} ${f.trialLengthDays === 1 ? "day" : "days"}`
    : "";
  if (len && inc) return `Your free trial ${len} and includes ${inc}.`;
  if (len) return `Your free trial ${len}.`;
  if (inc) return `Your free trial includes ${inc}.`;
  return "Your free trial is running.";
}

let cached: { uid: string; at: number; p: Promise<TrialFacts | null> } | null = null;

/** One read per account per minute, shared by every caller on the page. */
export function loadTrialFacts(force = false): Promise<TrialFacts | null> {
  return (async () => {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;
    if (!uid) return null;
    if (!force && cached && cached.uid === uid && Date.now() - cached.at < 60_000) return cached.p;
    const p = (async (): Promise<TrialFacts | null> => {
      const [{ data: c, error: cErr }, { data: prof, error: pErr }] = await Promise.all([
        supabase.from("user_credits_v2" as any)
          .select("tier, trial_ends_at, monthly_credit_grant")
          .eq("user_id", uid).maybeSingle(),
        supabase.from("profiles" as any).select("created_at").eq("id", uid).maybeSingle(),
      ]);
      if (cErr) return null; // say nothing rather than guess about someone's plan
      const row = (c as any) || {};
      const tier = row.tier ? String(row.tier).toLowerCase() : null;
      const ends = row.trial_ends_at ? String(row.trial_ends_at) : null;
      // An unreadable sign-up date only drops "runs N days" from the sentence.
      const created = !pErr && (prof as any)?.created_at ? new Date((prof as any).created_at).getTime() : NaN;
      const endMs = ends ? new Date(ends).getTime() : NaN;
      const len = Number.isFinite(created) && Number.isFinite(endMs) && endMs > created
        ? Math.round((endMs - created) / DAY_MS) : null;
      let watchMax: number | null = null;
      let watchMode: string | null = null;
      if (tier) {
        const { data: w, error: wErr } = await (supabase as any).from("watch_plan_limits")
          .select("max_companies, alert_mode").eq("plan", tier).maybeSingle();
        // An unreadable watch limit only drops the companies part of the sentence.
        if (!wErr && w) {
          watchMax = Number((w as any).max_companies) || null;
          watchMode = (w as any).alert_mode ? String((w as any).alert_mode) : null;
        }
      }
      const proFeatures = tier === "trial" && !!TIER_LIMITS.trial && !!TIER_LIMITS.pro
        && JSON.stringify(TIER_LIMITS.trial) === JSON.stringify(TIER_LIMITS.pro);
      const grant = row.monthly_credit_grant;
      return {
        tier,
        trialEndsAt: ends,
        trialLengthDays: len,
        proFeatures,
        watchMax,
        watchMode,
        credits: grant === null || grant === undefined ? null : Number(grant),
      };
    })();
    cached = { uid, at: Date.now(), p };
    return p;
  })();
}

/** React hook: the facts, or undefined while loading (null when unreadable or signed out). */
export function useTrialFacts(enabled = true): TrialFacts | null | undefined {
  const [facts, setFacts] = useState<TrialFacts | null | undefined>(undefined);
  useEffect(() => {
    if (!enabled) { setFacts(null); return; }
    let alive = true;
    loadTrialFacts().then((f) => { if (alive) setFacts(f); }).catch(() => { if (alive) setFacts(null); });
    return () => { alive = false; };
  }, [enabled]);
  return facts;
}

/** Is setup finished on this profile row (either completion column, as App.tsx reads it)? */
export function setupDone(profile: any): boolean {
  return profile?.onboarding_complete === true || profile?.onboarding_completed === true;
}

/**
 * Does the quiet first-screen line show? true / false, or undefined while the plan is loading.
 * No line for a finished setup or a paid account.
 */
export function quietLineShows(profile: any, facts: TrialFacts | null | undefined): boolean | undefined {
  if (!profile || setupDone(profile)) return false;
  if (facts === undefined) return undefined;
  if (facts && isPaidTier(facts.tier)) return false;
  return true;
}
